import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Cpu, Play, Plus, Save, Trash2 } from "lucide-react";
import { api, errMsg, TIMEFRAMES } from "@/lib/api";
import { RuleBlock } from "@/components/RuleBlock";

const BLANK = {
  name: "My Strategy", symbol: "NIFTY50", timeframe: "5m", product: "MIS",
  entry: { logic: "AND", conditions: [{ left: { type: "EMA", period: 9 }, op: "crosses_above", right: { type: "EMA", period: 21 } }] },
  exit: { logic: "OR", conditions: [{ left: { type: "EMA", period: 9 }, op: "crosses_below", right: { type: "EMA", period: 21 } }] },
  stop_loss_pct: 1, take_profit_pct: 2, capital: 1000000, position_size_pct: 50, charges_pct: 0.05, description: "", risk_level: "Medium",
};

const Field = ({ label, children }) => (
  <label className="flex flex-col gap-1">
    <span className="widget-title">{label}</span>
    {children}
  </label>
);

export default function StrategyBuilder({ onBacktest, goPaper }) {
  const [list, setList] = useState([]);
  const [symbols, setSymbols] = useState([]);
  const [s, setS] = useState(BLANK);
  const load = () => api.get("/strategies").then((r) => setList(r.data));

  useEffect(() => {
    load().then(() => {});
    api.get("/symbols").then((r) => setSymbols(r.data));
  }, []);
  useEffect(() => { if (!s.id && list.length && s === BLANK) setS(list[0]); }, [list, s]);

  const set = (k) => (e) => setS({ ...s, [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value });
  const body = () => { const { id, created_at, published, published_at, win_rate, stats, ...rest } = s; return rest; };

  const save = async () => {
    try {
      const { data } = s.id ? await api.put(`/strategies/${s.id}`, body()) : await api.post("/strategies", body());
      setS(data); await load(); toast.success("Strategy saved");
      return data;
    } catch (e) { toast.error(errMsg(e)); return null; }
  };
  const remove = async (id) => {
    await api.delete(`/strategies/${id}`);
    if (s.id === id) setS({ ...BLANK, name: "New Strategy" });
    load(); toast("Strategy deleted");
  };
  const deploy = async () => {
    const saved = await save();
    if (!saved) return;
    try { await api.post("/paper/bots", { strategy_id: saved.id }); toast.success(`Bot deployed: ${saved.name}`); goPaper(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-3 rise" data-testid="strategy-builder">
      <div className="panel h-fit">
        <div className="flex items-center justify-between px-3 py-2 border-b border-[#163056]">
          <span className="widget-title">Strategies</span>
          <button data-testid="strategy-new-button" onClick={() => setS({ ...BLANK, name: "New Strategy" })} className="text-[#22A34A] hover:text-white"><Plus className="w-4 h-4" /></button>
        </div>
        {list.map((x) => (
          <div key={x.id} className={`group flex items-center px-3 py-2 border-b border-[#102A4A] cursor-pointer transition-colors hover:bg-[#102A4A] ${s.id === x.id ? "bg-[#102A4A] border-l-2 border-l-[#22A34A]" : "border-l-2 border-l-transparent"}`}
            onClick={() => setS(x)} data-testid={`strategy-item-${x.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-slate-100 truncate">{x.name}</div>
              <div className="text-[10px] font-mono text-slate-500">{x.symbol} · {x.timeframe} · {x.product}</div>
            </div>
            <button data-testid={`strategy-delete-${x.id}`} onClick={(e) => { e.stopPropagation(); remove(x.id); }} className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-[#FF3B30] transition-opacity">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>

      <div className="space-y-3 min-w-0">
        <div className="panel p-3">
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <input data-testid="strategy-name-input" value={s.name} onChange={set("name")} className="bg-transparent font-display text-2xl font-bold text-white tracking-tight outline-none border-b border-transparent focus:border-[#22A34A] flex-1 min-w-[200px]" />
            <button data-testid="strategy-save-button" onClick={save} className="btn-ghost"><Save className="w-3.5 h-3.5" /> Save</button>
            <button data-testid="strategy-backtest-run-button" onClick={() => onBacktest(body())} className="btn-primary"><Play className="w-3.5 h-3.5" /> Run Backtest</button>
            <button data-testid="strategy-deploy-button" onClick={deploy} className="btn-bull"><Cpu className="w-3.5 h-3.5" /> Deploy to Paper</button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Symbol">
              <select data-testid="strategy-symbol-select" className="field" value={s.symbol} onChange={set("symbol")}>
                {symbols.map((x) => <option key={x.symbol} value={x.symbol}>{x.symbol}</option>)}
              </select>
            </Field>
            <Field label="Timeframe">
              <select data-testid="strategy-timeframe-select" className="field" value={s.timeframe} onChange={set("timeframe")}>
                {TIMEFRAMES.map((t) => <option key={t}>{t}</option>)}
              </select>
            </Field>
            <Field label="Product">
              <select data-testid="strategy-product-select" className="field" value={s.product} onChange={set("product")}>
                <option value="MIS">MIS (Intraday)</option><option value="CNC">CNC (Delivery)</option>
              </select>
            </Field>
            <Field label="Capital (₹)"><input data-testid="strategy-capital-input" type="number" className="field" value={s.capital} onChange={set("capital")} /></Field>
          </div>
        </div>

        <RuleBlock title="Entry Conditions · BUY" prefix="entry" accent="#00E676" block={s.entry} onChange={(b) => setS({ ...s, entry: b })} />
        <RuleBlock title="Exit Conditions · SELL" prefix="exit" accent="#FF3B30" block={s.exit} onChange={(b) => setS({ ...s, exit: b })} />

        <div className="panel p-3">
          <div className="widget-title mb-3">Risk & Position Sizing</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Field label="Stop Loss %"><input data-testid="strategy-sl-input" type="number" step="0.1" className="field" value={s.stop_loss_pct} onChange={set("stop_loss_pct")} /></Field>
            <Field label="Take Profit %"><input data-testid="strategy-tp-input" type="number" step="0.1" className="field" value={s.take_profit_pct} onChange={set("take_profit_pct")} /></Field>
            <Field label="Position Size % of Capital"><input data-testid="strategy-size-input" type="number" className="field" value={s.position_size_pct} onChange={set("position_size_pct")} /></Field>
            <Field label="Charges % / side"><input data-testid="strategy-charges-input" type="number" step="0.01" className="field" value={s.charges_pct} onChange={set("charges_pct")} /></Field>
          </div>
          <p className="text-[11px] text-slate-500 mt-3">Long-only. Entry/exit at bar close; SL/TP checked intrabar. 0 disables SL/TP.</p>
        </div>

        <div className="panel p-3" data-testid="catalog-details-panel">
          <div className="widget-title mb-3">Catalog Details (shown to clients)</div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_160px] gap-3">
            <Field label="Description"><textarea data-testid="strategy-description-input" className="field h-20 py-1.5 resize-none" value={s.description || ""} onChange={set("description")} placeholder="What does this strategy do, when does it trade, what to expect…" /></Field>
            <Field label="Risk level">
              <select data-testid="strategy-risk-select" className="field" value={s.risk_level || "Medium"} onChange={set("risk_level")}>
                <option>Low</option><option>Medium</option><option>High</option>
              </select>
            </Field>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">{s.published ? "Published · win rate is recomputed when you re-publish." : "Draft · publish from the Catalog tab to make it visible to clients."}</p>
        </div>
      </div>
    </div>
  );
}
